import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Server, Play, Database, CheckCircle2, XCircle, Loader2, Plus, RefreshCw, Wifi, WifiOff, Lock, Globe, Trash2, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SAPSimulation = ({ project }) => {
  const [activeSection, setActiveSection] = useState('simulation');
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [showCreateMock, setShowCreateMock] = useState(false);
  const [showCreateReal, setShowCreateReal] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [expandedResults, setExpandedResults] = useState({});

  useEffect(() => {
    fetchEnvironments();
  }, [project.id]);

  const fetchEnvironments = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/sap-environments`);
      setEnvironments(response.data);
    } catch (error) {
      console.error('Error fetching environments:', error);
    } finally {
      setLoading(false);
    }
  };

  const createEnvironment = async (envData) => {
    try {
      await axios.post(`${API}/sap-environments`, {
        ...envData,
        project_id: project.id
      });
      await fetchEnvironments();
      setShowCreateMock(false);
      setShowCreateReal(false);
    } catch (error) {
      console.error('Error creating environment:', error);
      alert(`Failed to create connection: ${error.response?.data?.detail || error.message}`);
    }
  };

  const updateEnvironment = async (envId, envData) => {
    try {
      await axios.put(`${API}/sap-environments/${envId}`, {
        ...envData,
        project_id: project.id
      });
      await fetchEnvironments();
      setEditingConnection(null);
      alert('✅ Connection updated successfully');
    } catch (error) {
      console.error('Error updating environment:', error);
      alert(`Failed to update connection: ${error.response?.data?.detail || error.message}`);
    }
  };

  const testConnection = async (envId) => {
    setConnecting(envId);
    try {
      const response = await axios.post(`${API}/sap-environments/${envId}/connect`, {});
      await fetchEnvironments();
      
      // Store test result
      setTestResults(prev => ({
        ...prev,
        [envId]: response.data
      }));
      
      // Auto-expand to show results
      setExpandedResults(prev => ({
        ...prev,
        [envId]: true
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error connecting:', error);
      
      // Store error result
      const errorResult = {
        success: false,
        error: error.response?.data?.detail || error.message,
        mode: 'ERROR'
      };
      
      setTestResults(prev => ({
        ...prev,
        [envId]: errorResult
      }));
      
      setExpandedResults(prev => ({
        ...prev,
        [envId]: true
      }));
    } finally {
      setConnecting(null);
    }
  };

  const deleteConnection = async (envId) => {
    if (!window.confirm('Are you sure you want to delete this SAP connection? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/sap-environments/${envId}`);
      await fetchEnvironments();
      alert('✅ Connection deleted successfully');
    } catch (error) {
      console.error('Error deleting connection:', error);
      alert(`❌ Failed to delete connection: ${error.response?.data?.detail || error.message}`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-12 h-12 text-blue-600 animate-spin" /></div>;
  }

  const mockEnvironments = environments.filter(e => e.is_mock);
  const realEnvironments = environments.filter(e => !e.is_mock);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">SAP Connection Manager</h2>
            <p className="text-gray-600">Configure mock simulation environments or connect to real SAP systems</p>
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveSection('simulation')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeSection === 'simulation'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            🎭 Simulation Environments
          </button>
          <button
            onClick={() => setActiveSection('real')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeSection === 'real'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            🔗 Real SAP Connections
          </button>
        </div>

        {/* Simulation Section */}
        {activeSection === 'simulation' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mock SAP Environments</h3>
                <p className="text-gray-600">Create simulated ECC and S/4HANA systems for testing and demos</p>
              </div>
              <button
                onClick={() => setShowCreateMock(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Mock Environment</span>
              </button>
            </div>

            {mockEnvironments.length === 0 ? (
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-12 text-center">
                <Database className="w-20 h-20 mx-auto mb-6 text-purple-300" />
                <h4 className="text-xl font-semibold text-gray-900 mb-3">No Mock Environments</h4>
                <p className="text-gray-600 mb-6">Create simulated SAP systems for testing without real SAP access</p>
                <button
                  onClick={() => setShowCreateMock(true)}
                  className="bg-purple-600 text-white px-8 py-3 rounded-xl hover:bg-purple-700 inline-flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Create Mock Environment</span>
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {mockEnvironments.map((env) => (
                  <div key={env.id} className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Database className="w-6 h-6 text-purple-600" />
                          <h3 className="text-xl font-bold text-gray-900">{env.environment_name}</h3>
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-semibold">MOCK</span>
                        </div>
                        <p className="text-sm text-gray-600">{env.system_type} - {env.system_id} / Client {env.client}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        env.connection_status === 'connected' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {env.connection_status || 'Ready'}
                      </span>
                    </div>
                    <button
                      onClick={() => testConnection(env.id)}
                      disabled={connecting === env.id}
                      className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      {connecting === env.id ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /><span>Testing...</span></>
                      ) : (
                        <><Play className="w-4 h-4" /><span>Test Connection</span></>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Real SAP Connection Section */}
        {activeSection === 'real' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Real SAP System Connections</h3>
                <p className="text-gray-600">Connect to actual ECC or S/4HANA systems using RFC or OData</p>
              </div>
              <button
                onClick={() => setShowCreateReal(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Real Connection</span>
              </button>
            </div>

            {realEnvironments.length === 0 ? (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-12 text-center">
                <Server className="w-20 h-20 mx-auto mb-6 text-blue-300" />
                <h4 className="text-xl font-semibold text-gray-900 mb-3">No Real Connections</h4>
                <p className="text-gray-600 mb-6">Configure RFC or OData connections to your SAP systems</p>
                <button
                  onClick={() => setShowCreateReal(true)}
                  className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 inline-flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Real Connection</span>
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {realEnvironments.map((env) => (
                  <div key={env.id} className="bg-white border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Server className="w-6 h-6 text-blue-600" />
                          <h3 className="text-xl font-bold text-gray-900">{env.environment_name}</h3>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">REAL</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{env.system_type} - {env.system_id} / Client {env.client}</p>
                        {env.host && <p className="text-xs text-gray-500">Host: {env.host}:{env.instance_number || '00'}</p>}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        env.connection_status === 'connected' ? 'bg-green-100 text-green-700' :
                        env.connection_status === 'error' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {env.connection_status || 'Not tested'}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => testConnection(env.id)}
                        disabled={connecting === env.id}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                      >
                        {connecting === env.id ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /><span>Connecting...</span></>
                        ) : (
                          <><Wifi className="w-4 h-4" /><span>Test Connection</span></>
                        )}
                      </button>
                      <button
                        onClick={() => setEditingConnection(env)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        title="Edit Connection"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteConnection(env.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        title="Delete Connection"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Test Results Display */}
                    {testResults[env.id] && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => setExpandedResults(prev => ({...prev, [env.id]: !prev[env.id]}))}
                          className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 hover:text-gray-900 mb-2"
                        >
                          <span>Connection Test Details</span>
                          <span>{expandedResults[env.id] ? '▼' : '▶'}</span>
                        </button>
                        
                        {expandedResults[env.id] && (
                          <div className="space-y-3">
                            {/* Status Banner */}
                            <div className={`p-3 rounded-lg ${
                              testResults[env.id].success 
                                ? 'bg-green-50 border border-green-200' 
                                : 'bg-red-50 border border-red-200'
                            }`}>
                              <div className="flex items-center space-x-2">
                                {testResults[env.id].success ? (
                                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                )}
                                <div className="flex-1">
                                  <p className={`font-bold text-sm ${
                                    testResults[env.id].success ? 'text-green-800' : 'text-red-800'
                                  }`}>
                                    {testResults[env.id].success ? 'Connection Successful' : 'Connection Failed'}
                                  </p>
                                  <p className="text-xs text-gray-600">Mode: {testResults[env.id].mode}</p>
                                </div>
                                {testResults[env.id].response_time_ms && (
                                  <span className="text-xs bg-white px-2 py-1 rounded">
                                    {testResults[env.id].response_time_ms}ms
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Error Message */}
                            {testResults[env.id].error && (
                              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                                <p className="text-sm font-semibold text-red-800 mb-1">Error:</p>
                                <p className="text-sm text-red-700">{testResults[env.id].error}</p>
                              </div>
                            )}
                            
                            {/* Success Details */}
                            {testResults[env.id].success && (
                              <div className="space-y-2">
                                {testResults[env.id].entity_sets && (
                                  <div className="p-3 bg-green-50 rounded-lg">
                                    <p className="text-xs font-semibold text-gray-700 mb-1">
                                      Entity Sets ({testResults[env.id].entity_count}):
                                    </p>
                                    <p className="text-xs text-gray-600">
                                      {testResults[env.id].entity_sets.slice(0, 5).join(', ')}
                                      {testResults[env.id].entity_sets.length > 5 && '...'}
                                    </p>
                                  </div>
                                )}
                                {testResults[env.id].available_operations && (
                                  <div className="p-3 bg-purple-50 rounded-lg">
                                    <p className="text-xs font-semibold text-gray-700 mb-1">
                                      SOAP Operations ({testResults[env.id].operation_count}):
                                    </p>
                                    <p className="text-xs text-gray-600">
                                      {testResults[env.id].available_operations.slice(0, 3).join(', ')}
                                      {testResults[env.id].available_operations.length > 3 && '...'}
                                    </p>
                                  </div>
                                )}
                                {testResults[env.id].system_info && (
                                  <div className="p-3 bg-blue-50 rounded-lg">
                                    <p className="text-xs font-semibold text-gray-700 mb-1">System Info:</p>
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div>SID: {testResults[env.id].system_info.system_id}</div>
                                      <div>Client: {testResults[env.id].system_info.client}</div>
                                      <div>Host: {testResults[env.id].system_info.host}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Diagnostics */}
                            {testResults[env.id].diagnostics && (
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-semibold text-gray-700 mb-2">Diagnostics:</p>
                                <div className="space-y-1">
                                  {Object.entries(testResults[env.id].diagnostics).map(([test, result]) => (
                                    result && (
                                      <div key={test} className="flex items-start space-x-2 text-xs">
                                        <span className={
                                          result.status === 'success' ? 'text-green-600' :
                                          result.status === 'failed' ? 'text-red-600' :
                                          'text-yellow-600'
                                        }>
                                          {result.status === 'success' ? '✅' : 
                                           result.status === 'failed' ? '❌' : '⚠️'}
                                        </span>
                                        <div className="flex-1">
                                          <span className="font-semibold capitalize">{test.replace(/_/g, ' ')}:</span>
                                          <span className="text-gray-600 ml-1">{result.message}</span>
                                        </div>
                                      </div>
                                    )
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Recommended Fixes */}
                            {testResults[env.id].fixes && testResults[env.id].fixes.length > 0 && (
                              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                <p className="text-xs font-semibold text-gray-700 mb-2">💡 Recommended Fixes:</p>
                                <div className="space-y-2">
                                  {testResults[env.id].fixes.map((fix, idx) => (
                                    <div key={idx} className="text-xs">
                                      <p className="font-semibold text-gray-800">
                                        Step {fix.step}: {fix.action}
                                      </p>
                                      <p className="text-gray-600 ml-4">{fix.details}</p>
                                      {fix.sap_transaction && (
                                        <p className="text-blue-600 ml-4">SAP: {fix.sap_transaction}</p>
                                      )}
                                      {fix.commands && fix.commands.length > 0 && (
                                        <div className="ml-4 mt-1 p-2 bg-gray-800 rounded text-green-400 font-mono">
                                          {fix.commands.filter(c => c.trim()).slice(0, 2).map((cmd, i) => (
                                            <div key={i}>{cmd}</div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Connection Parameters */}
                            {testResults[env.id].connection_params && (
                              <div className="p-3 bg-blue-50 rounded-lg">
                                <p className="text-xs font-semibold text-gray-700 mb-2">Connection Parameters:</p>
                                <div className="text-xs text-gray-600 space-y-1 font-mono">
                                  <div>Host: {testResults[env.id].connection_params.host}</div>
                                  <div>Instance: {testResults[env.id].connection_params.instance}</div>
                                  <div>Client: {testResults[env.id].connection_params.client}</div>
                                  <div>User: {testResults[env.id].connection_params.user}</div>
                                  <div>RFC Port: {testResults[env.id].connection_params.rfc_port}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Mock Environment Modal */}
      {showCreateMock && (
        <CreateMockEnvironmentModal
          onClose={() => setShowCreateMock(false)}
          onCreate={createEnvironment}
        />
      )}

      {/* Create Real Connection Modal */}
      {showCreateReal && (
        <CreateRealConnectionModal
          onClose={() => setShowCreateReal(false)}
          onCreate={createEnvironment}
        />
      )}
      
      {editingConnection && (
        <EditConnectionModal
          connection={editingConnection}
          onClose={() => setEditingConnection(null)}
          onUpdate={updateEnvironment}
        />
      )}
    </div>
  );
};

// Mock Environment Modal
const CreateMockEnvironmentModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    environment_name: '',
    system_type: 'ECC',
    system_id: 'DEV',
    client: '100',
    is_mock: true,
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Mock SAP Environment</h3>
        <form onSubmit={(e) => { e.preventDefault(); onCreate(formData); }} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Environment Name *</label>
            <input
              type="text"
              required
              value={formData.environment_name}
              onChange={(e) => setFormData({ ...formData, environment_name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
              placeholder="e.g., Mock ECC Development"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System Type</label>
              <select
                value={formData.system_type}
                onChange={(e) => setFormData({ ...formData, system_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
              >
                <option value="ECC">SAP ECC</option>
                <option value="S4HANA">S/4HANA</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System ID (SID)</label>
              <input
                type="text"
                value={formData.system_id}
                onChange={(e) => setFormData({ ...formData, system_id: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                placeholder="DEV"
                maxLength="3"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Client</label>
              <input
                type="text"
                value={formData.client}
                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                placeholder="100"
                maxLength="3"
              />
            </div>
          </div>
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> Mock environments use simulated SAP data for testing and demonstrations. 
              No real SAP connection required.
            </p>
          </div>
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t">
            <button type="button" onClick={onClose} className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg">Create Mock Environment</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Real Connection Modal
const CreateRealConnectionModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    environment_name: '',
    system_type: 'ECC',
    system_id: 'PRD',
    client: '100',
    host: '',
    instance_number: '00',
    connection_type: 'RFC',
    username: '',
    password: '',
    language: 'EN',
    odata_service_url: '',
    soap_wsdl_url: '',
    is_mock: false,
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Configure Real SAP Connection</h3>
        <form onSubmit={(e) => { e.preventDefault(); onCreate(formData); }} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Connection Name *</label>
            <input
              type="text"
              required
              value={formData.environment_name}
              onChange={(e) => setFormData({ ...formData, environment_name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Production ECC"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System Type</label>
              <select
                value={formData.system_type}
                onChange={(e) => setFormData({ ...formData, system_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
              >
                <option value="ECC">SAP ECC 6.0</option>
                <option value="S4HANA">SAP S/4HANA</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System ID (SID) *</label>
              <input
                type="text"
                required
                value={formData.system_id}
                onChange={(e) => setFormData({ ...formData, system_id: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                placeholder="PRD"
                maxLength="3"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Application Server Host *</label>
              <input
                type="text"
                required
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                placeholder="sapprd.company.com or 10.1.2.45"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Instance Number *</label>
              <input
                type="text"
                required
                value={formData.instance_number}
                onChange={(e) => setFormData({ ...formData, instance_number: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                placeholder="00"
                maxLength="2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Client *</label>
            <input
              type="text"
              required
              value={formData.client}
              onChange={(e) => setFormData({ ...formData, client: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
              placeholder="100"
              maxLength="3"
            />
          </div>

          {/* Connection Type Selection */}
          <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl">
            <h4 className="font-bold text-gray-900 mb-4">Connection Protocol</h4>
            <div className="grid grid-cols-3 gap-3">
              <label className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.connection_type === 'RFC'
                  ? 'border-blue-600 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-blue-300'
              }`}>
                <input
                  type="radio"
                  name="connection_type"
                  value="RFC"
                  checked={formData.connection_type === 'RFC'}
                  onChange={(e) => setFormData({ ...formData, connection_type: e.target.value })}
                  className="hidden"
                />
                <div className="text-center">
                  <Database className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-bold text-gray-900">RFC</p>
                  <p className="text-xs text-gray-600 mt-1">SAP NetWeaver RFC</p>
                  <p className="text-xs text-orange-600 mt-1">Requires SDK</p>
                </div>
              </label>
              
              <label className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.connection_type === 'OData'
                  ? 'border-green-600 bg-green-50 shadow-md'
                  : 'border-gray-200 hover:border-green-300'
              }`}>
                <input
                  type="radio"
                  name="connection_type"
                  value="OData"
                  checked={formData.connection_type === 'OData'}
                  onChange={(e) => setFormData({ ...formData, connection_type: e.target.value })}
                  className="hidden"
                />
                <div className="text-center">
                  <Globe className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="font-bold text-gray-900">OData</p>
                  <p className="text-xs text-gray-600 mt-1">REST/HTTP API</p>
                  <p className="text-xs text-green-600 mt-1">Recommended</p>
                </div>
              </label>
              
              <label className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.connection_type === 'SOAP'
                  ? 'border-purple-600 bg-purple-50 shadow-md'
                  : 'border-gray-200 hover:border-purple-300'
              }`}>
                <input
                  type="radio"
                  name="connection_type"
                  value="SOAP"
                  checked={formData.connection_type === 'SOAP'}
                  onChange={(e) => setFormData({ ...formData, connection_type: e.target.value })}
                  className="hidden"
                />
                <div className="text-center">
                  <Server className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <p className="font-bold text-gray-900">SOAP</p>
                  <p className="text-xs text-gray-600 mt-1">Web Services</p>
                  <p className="text-xs text-purple-600 mt-1">Legacy Support</p>
                </div>
              </label>
            </div>
          </div>

          {/* OData Configuration (shown only if OData selected) */}
          {formData.connection_type === 'OData' && (
            <div className="p-6 bg-green-50 border-2 border-green-200 rounded-xl">
              <h4 className="font-bold text-gray-900 mb-4">OData Service Configuration</h4>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">OData Service URL *</label>
                <input
                  type="url"
                  required
                  value={formData.odata_service_url}
                  onChange={(e) => setFormData({ ...formData, odata_service_url: e.target.value })}
                  placeholder="https://host:port/sap/opu/odata/sap/SERVICE_NAME"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
                />
                <p className="text-xs text-gray-600 mt-2">
                  Example: https://3.15.211.34:443/sap/opu/odata/sap/ZMD_SRV_01/
                </p>
              </div>
            </div>
          )}

          {/* SOAP Configuration (shown only if SOAP selected) */}
          {formData.connection_type === 'SOAP' && (
            <div className="p-6 bg-purple-50 border-2 border-purple-200 rounded-xl">
              <h4 className="font-bold text-gray-900 mb-4">SOAP Web Service Configuration</h4>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">WSDL URL *</label>
                <input
                  type="url"
                  required
                  value={formData.soap_wsdl_url}
                  onChange={(e) => setFormData({ ...formData, soap_wsdl_url: e.target.value })}
                  placeholder="https://host:port/sap/bc/srt/wsdl/...?sap-client=100"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                />
                <p className="text-xs text-gray-600 mt-2">
                  Example: https://3.15.211.34:443/sap/bc/srt/wsdl/flv_10002A111AD1/bndg_url/sap/bc/srt/rfc/sap/zrfc_system_info/...
                </p>
              </div>
            </div>
          )}

          {/* SAP RFC Credentials */}
          <div className="p-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
            <div className="flex items-center space-x-2 mb-4">
              <Lock className="w-5 h-5 text-yellow-600" />
              <h4 className="font-bold text-gray-900">SAP RFC Credentials</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">Required for real SAP system connection testing and data extraction</p>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">SAP Username *</label>
                <input
                  type="text"
                  required
                  value={formData.username || ''}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="RFC_USER"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">SAP Password *</label>
                <input
                  type="password"
                  required
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Language</label>
                <select
                  value={formData.language || 'EN'}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="EN">English (EN)</option>
                  <option value="DE">German (DE)</option>
                  <option value="FR">French (FR)</option>
                  <option value="ES">Spanish (ES)</option>
                  <option value="PT">Portuguese (PT)</option>
                  <option value="ZH">Chinese (ZH)</option>
                  <option value="JA">Japanese (JA)</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Credentials are encrypted and stored securely. Only used for RFC connection testing and authorized data extraction.
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <WifiOff className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">RFC Connection Requirements</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• SAP NetWeaver RFC SDK must be installed on AURA server</li>
                  <li>• Network connectivity to SAP system required (port 33NN where NN = instance)</li>
                  <li>• SAP user with read-only authorization (S_RFC, S_TABU_DIS)</li>
                  <li>• Credentials will be securely encrypted and stored</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Connection Details Preview</h4>
            <div className="space-y-2 text-sm font-mono">
              <div className="flex"><span className="text-gray-600 w-32">Connection:</span><span className="font-semibold">RFC (SAP NetWeaver)</span></div>
              <div className="flex"><span className="text-gray-600 w-32">Endpoint:</span><span className="font-semibold">{formData.host || '(not set)'}:{(parseInt(formData.instance_number) || 0) + 3300}</span></div>
              <div className="flex"><span className="text-gray-600 w-32">System:</span><span className="font-semibold">{formData.system_id}/{formData.client}</span></div>
              <div className="flex"><span className="text-gray-600 w-32">Mode:</span><span className="font-semibold text-blue-600">Read-Only</span></div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t">
            <button type="button" onClick={onClose} className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg">Add Connection</button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Edit Connection Modal
const EditConnectionModal = ({ connection, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    environment_name: connection.environment_name || '',
    system_type: connection.system_type || 'ECC',
    system_id: connection.system_id || 'PRD',
    client: connection.client || '100',
    host: connection.host || '',
    instance_number: connection.instance_number || '00',
    connection_type: connection.connection_type || 'RFC',
    username: connection.username || '',
    password: connection.password || '',
    language: connection.language || 'EN',
    odata_service_url: connection.odata_service_url || '',
    soap_wsdl_url: connection.soap_wsdl_url || '',
    is_mock: connection.is_mock || false,
  });
  const [updating, setUpdating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    await onUpdate(connection.id, formData);
    setUpdating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit SAP Connection</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Connection Name *</label>
            <input
              type="text"
              required
              value={formData.environment_name}
              onChange={(e) => setFormData({ ...formData, environment_name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Production ECC"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System Type</label>
              <select
                value={formData.system_type}
                onChange={(e) => setFormData({ ...formData, system_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
              >
                <option value="ECC">SAP ECC 6.0</option>
                <option value="S4HANA">SAP S/4HANA</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System ID (SID) *</label>
              <input
                type="text"
                required
                value={formData.system_id}
                onChange={(e) => setFormData({ ...formData, system_id: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                placeholder="PRD"
                maxLength="3"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Application Server Host *</label>
              <input
                type="text"
                required
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                placeholder="sapprd.company.com or 10.1.2.45"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Instance Number *</label>
              <input
                type="text"
                required
                value={formData.instance_number}
                onChange={(e) => setFormData({ ...formData, instance_number: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
                placeholder="00"
                maxLength="2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Client *</label>
            <input
              type="text"
              required
              value={formData.client}
              onChange={(e) => setFormData({ ...formData, client: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
              placeholder="100"
              maxLength="3"
            />
          </div>

          {/* Connection Type Selection */}
          <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl">
            <h4 className="font-bold text-gray-900 mb-4">Connection Protocol</h4>
            <div className="grid grid-cols-3 gap-3">
              <label className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.connection_type === 'RFC'
                  ? 'border-blue-600 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-blue-300'
              }`}>
                <input
                  type="radio"
                  name="connection_type"
                  value="RFC"
                  checked={formData.connection_type === 'RFC'}
                  onChange={(e) => setFormData({ ...formData, connection_type: e.target.value })}
                  className="hidden"
                />
                <div className="text-center">
                  <Database className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-bold text-gray-900">RFC</p>
                  <p className="text-xs text-gray-600 mt-1">SAP NetWeaver RFC</p>
                </div>
              </label>
              
              <label className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.connection_type === 'OData'
                  ? 'border-green-600 bg-green-50 shadow-md'
                  : 'border-gray-200 hover:border-green-300'
              }`}>
                <input
                  type="radio"
                  name="connection_type"
                  value="OData"
                  checked={formData.connection_type === 'OData'}
                  onChange={(e) => setFormData({ ...formData, connection_type: e.target.value })}
                  className="hidden"
                />
                <div className="text-center">
                  <Globe className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="font-bold text-gray-900">OData</p>
                  <p className="text-xs text-gray-600 mt-1">REST/HTTP API</p>
                </div>
              </label>
              
              <label className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.connection_type === 'SOAP'
                  ? 'border-purple-600 bg-purple-50 shadow-md'
                  : 'border-gray-200 hover:border-purple-300'
              }`}>
                <input
                  type="radio"
                  name="connection_type"
                  value="SOAP"
                  checked={formData.connection_type === 'SOAP'}
                  onChange={(e) => setFormData({ ...formData, connection_type: e.target.value })}
                  className="hidden"
                />
                <div className="text-center">
                  <Server className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <p className="font-bold text-gray-900">SOAP</p>
                  <p className="text-xs text-gray-600 mt-1">Web Services</p>
                </div>
              </label>
            </div>
          </div>

          {/* OData Configuration */}
          {formData.connection_type === 'OData' && (
            <div className="p-6 bg-green-50 border-2 border-green-200 rounded-xl">
              <h4 className="font-bold text-gray-900 mb-4">OData Service Configuration</h4>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">OData Service URL *</label>
                <input
                  type="url"
                  required={formData.connection_type === 'OData'}
                  value={formData.odata_service_url}
                  onChange={(e) => setFormData({ ...formData, odata_service_url: e.target.value })}
                  placeholder="https://host:port/sap/opu/odata/sap/SERVICE_NAME"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
                />
              </div>
            </div>
          )}

          {/* SOAP Configuration */}
          {formData.connection_type === 'SOAP' && (
            <div className="p-6 bg-purple-50 border-2 border-purple-200 rounded-xl">
              <h4 className="font-bold text-gray-900 mb-4">SOAP Web Service Configuration</h4>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">WSDL URL *</label>
                <input
                  type="url"
                  required={formData.connection_type === 'SOAP'}
                  value={formData.soap_wsdl_url}
                  onChange={(e) => setFormData({ ...formData, soap_wsdl_url: e.target.value })}
                  placeholder="https://host:port/sap/bc/srt/wsdl/...?sap-client=100"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                />
              </div>
            </div>
          )}

          {/* SAP Credentials */}
          <div className="p-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
            <div className="flex items-center space-x-2 mb-4">
              <Lock className="w-5 h-5 text-yellow-600" />
              <h4 className="font-bold text-gray-900">SAP Credentials</h4>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">SAP Username *</label>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="RFC_USER"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">SAP Password *</label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Language</label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="EN">English (EN)</option>
                  <option value="DE">German (DE)</option>
                  <option value="FR">French (FR)</option>
                  <option value="ES">Spanish (ES)</option>
                  <option value="PT">Portuguese (PT)</option>
                  <option value="ZH">Chinese (ZH)</option>
                  <option value="JA">Japanese (JA)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t">
            <button type="button" onClick={onClose} className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={updating}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 flex items-center space-x-2"
            >
              {updating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /><span>Updating...</span></>
              ) : (
                <><CheckCircle2 className="w-4 h-4" /><span>Update Connection</span></>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default SAPSimulation;